import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface AppSettings {
  floorplan_mask_padding_pct: number;
  floorplan_mask_feather_pct: number;
  stage_refinement_enabled: boolean;
  stage_refine_template_variant: 'pink' | 'mask';
  disabled_features: string[];
  catalog_staging_enabled: boolean; // deprecated: durch catalog_staging_admin/_live ersetzt
  // Admin/Live-Split (Migration 094): konsumiert als `isAdmin ? _admin : _live`.
  catalog_staging_admin: boolean;
  catalog_staging_live: boolean;
  stage_refinement_admin: boolean;
  stage_refinement_live: boolean;
  // Grundriss-2D-Vision-Analyst (Migration 099): konsumiert als `isAdmin ? _admin : _live`.
  floorplan_consult_admin: boolean;
  floorplan_consult_live: boolean;
  // Sanitär-Vektorsymbole (Migration 102): konsumiert als `isAdmin ? _admin : _live`.
  floorplan_fixture_symbols_admin: boolean;
  floorplan_fixture_symbols_live: boolean;
  // Flow v3: Empty-first Projekt-Assistent + Workspace-Tabs (Migration 104).
  flow_v3_admin: boolean;
  flow_v3_live: boolean;
  // Grundriss-Reprompting v2 (Nano Banana, Migration 106): konsumiert als `isAdmin ? _admin : _live`.
  floorplan_refine_v2_admin: boolean;
  floorplan_refine_v2_live: boolean;
  // Leerräum-Berater (Vision-Analyst fürs Leerräumen-Prompt): konsumiert als `isAdmin ? _admin : _live`.
  empty_consult_admin: boolean;
  empty_consult_live: boolean;
  // Support-Chat (Migration 121): konsumiert als `isAdmin ? _admin : _live`.
  support_chat_admin: boolean;
  support_chat_live: boolean;
}

export const DEFAULTS: AppSettings = {
  floorplan_mask_padding_pct: 1.5,
  floorplan_mask_feather_pct: 0.3,
  stage_refinement_enabled: false,
  stage_refine_template_variant: 'pink',
  disabled_features: [],
  catalog_staging_enabled: false,
  catalog_staging_admin: true,
  catalog_staging_live: false,
  stage_refinement_admin: true,
  stage_refinement_live: false,
  floorplan_consult_admin: true,
  floorplan_consult_live: false,
  floorplan_fixture_symbols_admin: true,
  floorplan_fixture_symbols_live: false,
  flow_v3_admin: true,
  flow_v3_live: false,
  floorplan_refine_v2_admin: true,
  floorplan_refine_v2_live: false,
  empty_consult_admin: true,
  empty_consult_live: false,
  support_chat_admin: true,
  support_chat_live: false,
};

export function useAppSettings(): { settings: AppSettings; loading: boolean } {
  const [settings, setSettings] = useState<AppSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .eq('id', 1)
        .single();

      if (error) {
        console.warn('[useAppSettings] fetch failed, using defaults', error);
      } else if (mounted && data) {
        setSettings({ ...DEFAULTS, ...data });
      }
      if (mounted) setLoading(false);
    };

    fetchSettings();

    // Realtime: react to admin updates without page reload
    const channel = supabase
      .channel('app_settings_singleton')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'app_settings', filter: 'id=eq.1' },
        (payload) => {
          if (mounted) setSettings({ ...DEFAULTS, ...(payload.new as Partial<AppSettings>) });
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      void supabase.removeChannel(channel);
    };
  }, []);

  return { settings, loading };
}
